import { Alert, Box, Button, Collapse, Stack, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { com } from '@eidu/form'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import { logException } from '../../util/Logging'
import usePermissionGrantRepository from '../../io/usePermissionGrantRepository'
import { requireNotUndefinedOrNull } from '../../util/require'
import PermissionGrantRepository from '../../io/PermissionGrantRepository'
import usePermittedActions from '../../io/usePermittedActions'
import PermissionError from '../entity/PermissionError'
import { PermissionWithId } from '../../components/input/PermissionsInput'
import ConfirmationDialog from '../../components/feedback/ConfirmationDialog'
import PermissionGrantForm from '../../components/layout/PermissionGrantForm'
import { useSnackBar } from '../../components/feedback/SnackBarProvider'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import permissionGrantIdFromString = com.eidu.sharedlib.entity.permission.permissionGrantIdFromString
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import PermittedGlobalAction = com.eidu.sharedlib.entity.permission.PermittedGlobalAction
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind

type CreateOrEditPermissionGrantPageContentProps = {
  entityTypes: readonly EntityType[]
  userSelector: EntitySelector
  setUserSelector: (id: EntitySelector) => void
  selectedPermissions: readonly PermissionWithId[]
  setSelectedPermissions: (permissions: readonly PermissionWithId[]) => void
  onSubmit: () => Promise<void>
  onDelete?: () => Promise<void>
  onCancel: () => void
  isEditMode: boolean
}

const CreateOrEditPermissionGrantPageContent = ({
  entityTypes,
  userSelector,
  setUserSelector,
  selectedPermissions,
  setSelectedPermissions,
  onSubmit,
  onDelete,
  onCancel,
  isEditMode,
}: CreateOrEditPermissionGrantPageContentProps) => (
  <Stack padding={3}>
    <Stack direction="row" spacing={1} justifyContent="space-between">
      <Typography variant="h4">{isEditMode ? 'Update permission' : 'Grant permission'}</Typography>
      {isEditMode && onDelete && (
        <ConfirmationDialog
          title="Are you sure you want to delete these permissions? This action cannot be undone."
          content={<Typography>Delete</Typography>}
          onConfirm={onDelete}
        />
      )}
    </Stack>
    <Box sx={{ paddingY: 2 }}>
      <PermissionGrantForm
        entityTypes={entityTypes}
        userSelector={userSelector}
        setUserSelector={setUserSelector}
        selectedPermissions={selectedPermissions}
        setSelectedPermissions={setSelectedPermissions}
      />
    </Box>
    <Stack direction="row" spacing={2} sx={{ padding: 2, borderRadius: 1, justifyContent: 'flex-end' }}>
      <Button color="primary" variant="contained" onClick={onCancel}>
        Cancel
      </Button>
      <Button color="primary" variant="contained" onClick={onSubmit}>
        Save
      </Button>
    </Stack>
  </Stack>
)

type CreateOrEditPermissionGrantPageProps = {
  permissionGrantRepository: PermissionGrantRepository
  entityTypes: readonly EntityType[]
  isEditMode: boolean
}

const CreateOrEditPermissionGrantPage = ({
  permissionGrantRepository,
  entityTypes,
  isEditMode,
}: CreateOrEditPermissionGrantPageProps) => {
  const navigate = useNavigate()
  const { showSnackBar } = useSnackBar()
  const params = useParams()

  const permissionGrantId = isEditMode
    ? permissionGrantIdFromString(requireNotUndefinedOrNull(params.permissionGrantId))
    : undefined
  const [submitting, setSubmitting] = useState(false)

  const permissionGrant = permissionGrantId ? permissionGrantRepository.useItem(permissionGrantId) : undefined
  const [userSelector, setUserSelector] = useState<EntitySelector>(
    new EntitySelector.OfType(
      entityTypes.filter((type) => type.kind === EntityTypeKind.User).sort((a, b) => a.name.localeCompare(b.name))[0].id
    )
  )
  const [selectedPermissions, setSelectedPermissions] = useState<readonly PermissionWithId[]>([])

  const requested = useRef([new PermittedGlobalAction(GlobalAction.ManagePermissionGrants)])
  const [permissions] = usePermittedActions(requested.current)

  const hasManagePermissionGrantsPermission = permissions?.some(
    (permittedAction) => permittedAction.action === GlobalAction.ManagePermissionGrants
  )

  useEffect(() => {
    if (isEditMode && permissionGrant) {
      setUserSelector(permissionGrant.permissionGrant.userSelector)
      setSelectedPermissions(
        [...permissionGrant.permissionGrant.permissions.asJsReadonlySetView()].map((permission) => ({
          id: crypto.randomUUID(),
          permission,
        }))
      )
    }
  }, [isEditMode, permissionGrant])

  const submit = useCallback(async () => {
    setSubmitting(true)
    try {
      if (userSelector) {
        if (isEditMode && permissionGrantId) {
          await permissionGrantRepository.modifyPermissionGrant(
            permissionGrantId,
            userSelector,
            selectedPermissions.map(({ permission }) => permission)
          )
          showSnackBar(`Permission Updated`, 'success')
        } else {
          await permissionGrantRepository.createPermissionGrant(
            userSelector,
            selectedPermissions.map(({ permission }) => permission)
          )
          showSnackBar(`Permission Created`, 'success')
        }
        navigate(-1)
      }
    } catch (e) {
      logException(e)
      showSnackBar(`Failed to ${isEditMode ? 'update' : 'grant'}`, 'error')
    } finally {
      setSubmitting(false)
    }
  }, [userSelector, selectedPermissions, isEditMode, permissionGrantId])

  const onCancel = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const onDelete = useCallback(async () => {
    setSubmitting(true)
    try {
      if (isEditMode && permissionGrantId) {
        await permissionGrantRepository.deletePermissionGrant(permissionGrantId)
        showSnackBar(`Deleted permission`, 'success')
        navigate(-1)
      }
    } catch (e) {
      logException(e)
      showSnackBar(`Failed to delete permission`, 'error')
    } finally {
      setSubmitting(false)
    }
  }, [isEditMode, permissionGrantId, permissionGrantRepository])

  const error = permissionGrantRepository.useError()
  const loading = permissionGrantRepository.useLoading()

  const loadingOverlayOpen = loading || submitting

  return (
    <Stack>
      <Collapse in={!!error}>
        <Alert
          variant="filled"
          severity="error"
          sx={{ margin: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={async () => {
                await permissionGrantRepository.clearError()
                await permissionGrantRepository.refreshAll()
              }}
            >
              Refresh
            </Button>
          }
        >
          <Box>{String(error)}</Box>
          <Box>Please reload the page and try again.</Box>
        </Alert>
      </Collapse>
      {!loading && !error && hasManagePermissionGrantsPermission && (
        <CreateOrEditPermissionGrantPageContent
          entityTypes={entityTypes}
          userSelector={userSelector}
          setUserSelector={setUserSelector}
          selectedPermissions={selectedPermissions}
          setSelectedPermissions={setSelectedPermissions}
          onSubmit={submit}
          onDelete={isEditMode ? onDelete : undefined}
          onCancel={onCancel}
          isEditMode={isEditMode}
        />
      )}
      <LoadingOverlay isOpen={loadingOverlayOpen} />
      {!loadingOverlayOpen && !hasManagePermissionGrantsPermission && (
        <PermissionError missingPermittedActions={[GlobalAction.ManagePermissionGrants]} />
      )}
    </Stack>
  )
}

const CreateOrEditPermissionGrantPageRoute = ({ isEditMode }: { isEditMode: boolean }) => {
  const entityTypeRepository = useContext(EntityTypesContext)
  const entityTypes = [...(entityTypeRepository.useAll()?.values() ?? [])]
  const permissionGrantRepository = usePermissionGrantRepository()

  if (entityTypes.length === 0) {
    return <LoadingOverlay isOpen />
  } else {
    return (
      <CreateOrEditPermissionGrantPage
        entityTypes={entityTypes}
        permissionGrantRepository={permissionGrantRepository}
        isEditMode={isEditMode}
      />
    )
  }
}

export { CreateOrEditPermissionGrantPage }
export default CreateOrEditPermissionGrantPageRoute
